<template>
  <!--
    A binary sensor, drawn as a *technical drawing in the pump's glass idiom*:
    one ink (`currentColor`), a couple of fixed glass tones, and thin frame
    strokes on the 48-grid. What the sensor caught is drawn as extra ink, never
    as a highlight — except the two things whose colour is the whole point of
    them: the water a leak leaves and the gas cloud. The hazards (smoke, gas,
    flame, water) are the *room* they happen in, not a detector: an empty room is
    motion, and the room with the hazard in it is the alarm.

    `active` is the sensor's own state on the hub. `generic` draws nothing on
    purpose — the tile keeps the device's own glyph.
  -->
  <svg
    class="sensor-anim"
    :class="[`sensor-anim--${kind}`, { 'sensor-anim--active': active }]"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <!-- ── Motion: an empty room, greyed, and only a walker once it reads ──── -->
    <g v-if="kind === 'motion'">
      <path class="sensor-anim__room" d="M9.6,38.6 V12.6 H38.4 V38.6 Z" />
      <path class="sensor-anim__room-floor" d="M9.6,32.4 H38.4 V38.6 H9.6 Z" />
      <g v-if="active" class="sensor-anim__person">
        <circle class="sensor-anim__party" cx="23.4" cy="21.8" r="2.6" />
        <path class="sensor-anim__line" d="M22.8,25.4 L21.8,31.4" />
        <path class="sensor-anim__line" d="M21.8,31.4 L18.4,38.4" />
        <path class="sensor-anim__line" d="M21.8,31.4 L26.2,37.6" />
        <path class="sensor-anim__line" d="M22.6,26.8 L27.4,29.4" />
        <path class="sensor-anim__line" d="M22.6,26.8 L17.8,24.8" />
      </g>
    </g>

    <!-- ── Door: the covers' plate for its wall, the opening's reveal, and one
         leaf — a light face with a small handle, hinged on the left and swung
         into the room when it reads open. ──────────────────────────────────── -->
    <g v-else-if="kind === 'door'">
      <rect class="sensor-anim__plate" x="2.5" y="2.5" width="43" height="43" rx="2.4" />
      <rect class="sensor-anim__opening" x="13.5" y="4.5" width="21" height="38" rx="1.2" />
      <polygon class="sensor-anim__leaf" :points="leaf.points" />
      <polygon class="sensor-anim__sash" :points="leaf.points" />
      <rect class="sensor-anim__handle" :x="leaf.right - 4.6" y="22.6" width="4.2" height="1.8" rx="0.5" />
      <rect class="sensor-anim__rail" x="11.5" y="42.5" width="25" height="1.6" rx="0.4" />
    </g>

    <!-- ── Window: one casement in a square recess, the door's swing in glass —
         closed it fills the opening, open it comes in from its left stile with
         the small handle and the sill. ─────────────────────────────────────── -->
    <g v-else-if="kind === 'window'">
      <rect class="sensor-anim__plate" x="2.5" y="2.5" width="43" height="43" rx="2.4" />
      <rect class="sensor-anim__opening" x="11.5" y="11" width="25" height="25" rx="1.2" />
      <polygon class="sensor-anim__pane" :points="sash.points" />
      <polygon class="sensor-anim__sash" :points="sash.points" />
      <rect class="sensor-anim__handle" :x="sash.right - 4.6" y="22.6" width="4.2" height="1.8" rx="0.5" />
      <rect class="sensor-anim__rail" x="9.5" y="36" width="29" height="1.6" rx="0.4" />
    </g>

    <!-- ── Roof window: the covers' own Velux drawing, tip and all — the sash is
         top-hinged, so it lifts from the sill and narrows while the head stays
         on the hinge. ────────────────────────────────────────────────────────── -->
    <g v-else-if="kind === 'roof_window'">
      <rect class="sensor-anim__plate" x="2.5" y="2.5" width="43" height="43" rx="2.4" />
      <rect class="sensor-anim__opening" x="12" y="7.5" width="24" height="33" rx="1.2" />
      <polygon class="sensor-anim__pane" :points="roofSash.points" />
      <polygon class="sensor-anim__sash" :points="roofSash.points" />
      <rect
        class="sensor-anim__rail"
        :x="roofSash.left"
        :y="roofSash.bottom - 1.5"
        :width="roofSash.width"
        height="1.5"
        rx="0.4" />
      <rect class="sensor-anim__handle" x="21" :y="roofSash.bottom - 1.1" width="6" height="1.8" rx="0.5" />
      <line class="sensor-anim__hinge" x1="12" y1="7.5" x2="36" y2="7.5" />
    </g>

    <!-- ── Contact: two glass halves — the reed on the frame, the magnet on the
         door — linked while they face and apart when the contact breaks. ───── -->
    <g v-else-if="kind === 'contact'">
      <rect class="sensor-anim__plate" x="2.5" y="2.5" width="43" height="43" rx="2.4" />
      <rect class="sensor-anim__pane" x="8.5" y="16" width="13" height="16" rx="2.8" />
      <rect class="sensor-anim__sash" x="8.5" y="16" width="13" height="16" rx="2.8" />
      <circle class="sensor-anim__handle" cx="15" cy="24" r="1.7" />
      <rect class="sensor-anim__pane" :x="active ? 28.4 : 24.2" y="16" width="13" height="16" rx="2.8" />
      <rect class="sensor-anim__sash" :x="active ? 28.4 : 24.2" y="16" width="13" height="16" rx="2.8" />
      <path v-if="!active" class="sensor-anim__trace" d="M22.4,24 H23.6" />
    </g>

    <!-- ── Smoke: the room, filled. No detector is drawn — the drawing is the
         smoke itself, hazing the air it sits in. ───────────────────────────── -->
    <g v-else-if="kind === 'smoke'">
      <path class="sensor-anim__room" d="M9.6,38.6 V12.6 H38.4 V38.6 Z" />
      <path class="sensor-anim__room-floor" d="M9.6,32.4 H38.4 V38.6 H9.6 Z" />
      <path class="sensor-anim__haze" d="M9.6,12.6 H38.4 V38.6 H9.6 Z" />
      <ellipse class="sensor-anim__mist" cx="24" cy="19" rx="12.8" ry="5.8" />
      <ellipse class="sensor-anim__mist sensor-anim__mist--b" cx="16.8" cy="25.6" rx="8.2" ry="4.6" />
      <ellipse class="sensor-anim__mist sensor-anim__mist--c" cx="30.6" cy="26.8" rx="7.4" ry="4.2" />
      <ellipse class="sensor-anim__mist" cx="23.4" cy="32.6" rx="10.6" ry="4.4" />
    </g>

    <!-- ── Flame: the room, and what is burning in it — translucent tongues off
         the floor, the burn showing through itself. ────────────────────────── -->
    <g v-else-if="kind === 'flame'">
      <path class="sensor-anim__room" d="M9.6,38.6 V12.6 H38.4 V38.6 Z" />
      <path class="sensor-anim__room-floor" d="M9.6,32.4 H38.4 V38.6 H9.6 Z" />
      <ellipse class="sensor-anim__flame-glow" cx="24" cy="35.4" rx="11.4" ry="2.6" />
      <g transform="translate(24,34.6) scale(0.6) translate(-24,-35)">
        <path
          class="sensor-anim__flame"
          d="M24,6.2 C29,13 33,18 33,26 A9,9 0 0 1 15,26 C15,18.4 19.4,14.4 22,9.2 C22,14.6 23.4,17.4 25,18.2 C25.6,14 25.2,9.8 24,6.2 Z" />
        <path
          class="sensor-anim__flame-core"
          d="M24,22 C26.6,26.4 27.6,28.6 27.6,30.6 A3.6,3.6 0 0 1 20.4,30.6 C20.4,28.6 21.6,26.6 24,22 Z" />
      </g>
      <g transform="translate(15,34.6) scale(0.38) translate(-24,-35)">
        <path
          class="sensor-anim__flame"
          d="M24,6.2 C29,13 33,18 33,26 A9,9 0 0 1 15,26 C15,18.4 19.4,14.4 22,9.2 C22,14.6 23.4,17.4 25,18.2 C25.6,14 25.2,9.8 24,6.2 Z" />
      </g>
      <g transform="translate(33,34.6) scale(0.34) translate(-24,-35)">
        <path
          class="sensor-anim__flame"
          d="M24,6.2 C29,13 33,18 33,26 A9,9 0 0 1 15,26 C15,18.4 19.4,14.4 22,9.2 C22,14.6 23.4,17.4 25,18.2 C25.6,14 25.2,9.8 24,6.2 Z" />
      </g>
    </g>

    <!-- ── Gas: the same room, with the cloud in it — yellow, because a gas
         cloud says so before anything else does. ───────────────────────────── -->
    <g v-else-if="kind === 'gas'">
      <path class="sensor-anim__room" d="M9.6,38.6 V12.6 H38.4 V38.6 Z" />
      <path class="sensor-anim__room-floor" d="M9.6,32.4 H38.4 V38.6 H9.6 Z" />
      <ellipse class="sensor-anim__mist sensor-anim__mist--gas" cx="24" cy="27.6" rx="12.6" ry="6.2" />
      <ellipse
        class="sensor-anim__mist sensor-anim__mist--gas sensor-anim__mist--b"
        cx="18"
        cy="20.6"
        rx="8.4"
        ry="4.8" />
      <ellipse
        class="sensor-anim__mist sensor-anim__mist--gas sensor-anim__mist--c"
        cx="30.4"
        cy="22"
        rx="7.6"
        ry="4.4" />
      <ellipse class="sensor-anim__mist sensor-anim__mist--gas" cx="24" cy="34.6" rx="11" ry="4" />
    </g>

    <!-- ── Leak: the room with water standing on its floor — no sensor, just the
         flood, its surface and the ripples spreading across it. ────────────── -->
    <g v-else-if="kind === 'leak'">
      <path class="sensor-anim__room" d="M9.6,38.6 V12.6 H38.4 V38.6 Z" />
      <path class="sensor-anim__room-floor" d="M9.6,32.4 H38.4 V38.6 H9.6 Z" />
      <path class="sensor-anim__flood" d="M9.6,31.8 H38.4 V38.6 H9.6 Z" />
      <path class="sensor-anim__flood-surface" d="M9.6,31.8 H38.4" />
      <path class="sensor-anim__flow" d="M12,33.4 H36" />
      <ellipse class="sensor-anim__ripple" cx="24" cy="35" rx="10.4" ry="2.6" />
      <ellipse class="sensor-anim__ripple sensor-anim__ripple--b" cx="24" cy="35" rx="10.4" ry="2.6" />
    </g>

    <!-- ── Vibration: one glass case, its trace, and the shock leaving it on both
         sides — nothing else. ──────────────────────────────────────────────── -->
    <g v-else-if="kind === 'vibration'">
      <rect class="sensor-anim__plate" x="2.5" y="2.5" width="43" height="43" rx="2.4" />
      <rect class="sensor-anim__pane" x="16" y="13" width="16" height="22" rx="3.4" />
      <rect class="sensor-anim__sash" x="16" y="13" width="16" height="22" rx="3.4" />
      <path class="sensor-anim__trace" d="M19.4,27.8 L21.8,20.2 L24.4,28 L26.8,21.2 L28.8,26.4" />
      <g class="sensor-anim__trace" :class="{ 'sensor-anim__trace--on': active }">
        <path d="M11.6,20.4 H13.8" />
        <path d="M11.6,27.6 H13.8" />
        <path d="M34.2,20.4 H36.4" />
        <path d="M34.2,27.6 H36.4" />
      </g>
    </g>

    <!-- ── Tamper: a glass case with its lid lifted clear of it. ─────────────── -->
    <g v-else-if="kind === 'tamper'">
      <rect class="sensor-anim__plate" x="2.5" y="2.5" width="43" height="43" rx="2.4" />
      <rect class="sensor-anim__pane" x="14" y="25" width="20" height="14" rx="2.8" />
      <rect class="sensor-anim__sash" x="14" y="25" width="20" height="14" rx="2.8" />
      <circle class="sensor-anim__handle" cx="24" cy="32" r="2" />
      <g :transform="active ? 'rotate(-12 25 14.5) translate(0 -2.4)' : 'rotate(-7 25 14.5)'">
        <rect class="sensor-anim__pane" x="15" y="9.6" width="20" height="10" rx="2.4" />
        <rect class="sensor-anim__sash" x="15" y="9.6" width="20" height="10" rx="2.4" />
      </g>
      <path class="sensor-anim__trace" d="M17.4,21.6 V24 M32.6,21.6 V24" />
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type { BinarySensorKind } from '../deviceWidgetTypes';

const props = withDefaults(
  defineProps<{
    kind?: BinarySensorKind;
    /** The sensor reads active on the hub — the openings ajar, the accent lit. */
    active?: boolean;
  }>(),
  { kind: 'generic', active: false },
);

/** The window's opening — square, the way a casement reads on this grid. */
const WINDOW = { x: 11.5, y: 11, w: 25, h: 25 };
/** The roof window's opening — the covers' own portrait numbers. */
const ROOF = { x: 12, y: 7.5, w: 24, h: 33 };
/** The door's opening: the same plate, a taller hole. */
const DOOR = { x: 13.5, y: 4.5, w: 21, h: 38 };

/**
 * A sash or a leaf hinged on its **left stile**, the way a door swings: closed it
 * fills the opening, and open it comes in — 40 % narrower and 22 % shorter at the
 * free edge, so the reveal shows beside it.
 */
const swung = (box: { x: number; y: number; w: number; h: number }) => {
  const open = props.active ? 1 : 0;
  const right = box.x + box.w * (1 - 0.4 * open);
  const lift = (box.h / 2) * 0.22 * open;
  return {
    right,
    points: `${box.x},${box.y} ${right},${box.y + lift} ${right},${box.y + box.h - lift} ${box.x},${box.y + box.h}`,
  };
};

const sash = computed(() => swung(WINDOW));
const leaf = computed(() => swung(DOOR));

/**
 * The roof window's sash, exactly as the covers draw it: top-hinged, so closed it
 * fills the opening and open its sill rises by 42 % and narrows by 22 % while the
 * head stays on the hinge.
 */
const roofSash = computed(() => {
  const open = props.active ? 1 : 0;
  const bottom = ROOF.y + ROOF.h * (1 - 0.42 * open);
  const inset = (ROOF.w / 2) * 0.22 * open;
  const left = ROOF.x + inset;
  const right = ROOF.x + ROOF.w - inset;
  return {
    bottom,
    left,
    width: right - left,
    points: `${ROOF.x},${ROOF.y} ${ROOF.x + ROOF.w},${ROOF.y} ${right},${bottom} ${left},${bottom}`,
  };
});
</script>

<style scoped>
.sensor-anim {
  display: block;
  width: 100%;
  height: 100%;
}

/* A monochrome drawing language: ink weight, fill depth and one recess tone.
   The only hues in the set are the two that are hues by nature — water, and the
   gas cloud — so the widget's own state colour stays the only other colour. */
.sensor-anim__line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* A monochrome drawing language: ink weight, fill depth and one recess tone.
   The only hues in the set are the two that are hues by nature — water, and the
   gas cloud — so the widget's own state colour stays the only other colour. */
.sensor-anim__line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.sensor-anim__party {
  fill: currentColor;
}

/* ── The window and the door, in the covers' own tones: the same soft slate
      plate is the roof (or the wall), the opening is the reveal, the window is
      glass, the door a light leaf, and the frame stroke, rail and handle are the
      covers' light metalwork. ──────────────────────────────────────────────── */
.sensor-anim__plate {
  fill: rgb(148 163 184 / 0.16);
  stroke: rgb(148 163 184 / 0.32);
  stroke-width: 0.6;
}

.sensor-anim__opening {
  fill: rgb(2 6 23 / 0.55);
  stroke: rgb(255 255 255 / 0.1);
  stroke-width: 0.4;
}

.sensor-anim__pane {
  fill: rgb(158 186 214 / 0.32);
}

.sensor-anim__leaf {
  fill: color-mix(in srgb, currentColor 8%, rgb(185 194 207));
}

.sensor-anim__sash {
  fill: none;
  stroke: color-mix(in srgb, currentColor 10%, rgb(198 206 217));
  stroke-width: 1.6;
  stroke-linejoin: round;
}

.sensor-anim__rail {
  fill: color-mix(in srgb, currentColor 10%, rgb(163 173 187));
}

.sensor-anim__handle {
  fill: color-mix(in srgb, currentColor 10%, rgb(198 206 217));
}

.sensor-anim__hinge {
  stroke: color-mix(in srgb, currentColor 10%, rgb(198 206 217));
  stroke-width: 1.4;
  stroke-linecap: round;
}

/* A detail line on the glass — the vibration trace, the lift ticks, the link
   between the two halves of a contact. Light metalwork; it darkens when the
   sensor reads, because then it is the state talking. */
.sensor-anim__trace {
  fill: none;
  stroke: color-mix(in srgb, currentColor 10%, rgb(198 206 217));
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: stroke 0.3s ease;
}

.sensor-anim__trace--on {
  stroke: currentColor;
}

/* ── Motion: the room the sensor watches — an empty greyed box, and a walker
      that only exists once it reads. ──────────────────────────────────────── */
.sensor-anim__room {
  fill: color-mix(in srgb, currentColor 6%, transparent);
  stroke: color-mix(in srgb, currentColor 44%, transparent);
  stroke-width: 1.3;
  stroke-linejoin: round;
}

.sensor-anim__room-floor {
  fill: color-mix(in srgb, currentColor 12%, transparent);
  stroke: none;
}

.sensor-anim__person {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sensor-anim--active .sensor-anim__person {
  opacity: 1;
}

/* ── Mist: what a smoke or gas alarm sees. Never an outline — a soft cloud
      stacked from faint discs, so it reads as smoke and not as a shape. A clear
      room shows none of it: the room alone is the idle drawing. ───────────── */
.sensor-anim__haze {
  fill: color-mix(in srgb, currentColor 9%, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sensor-anim--active .sensor-anim__haze {
  opacity: 1;
}

.sensor-anim__mist {
  fill: currentColor;
  opacity: 0;
  filter: blur(0.6px);
  transition: opacity 0.3s ease;
}

.sensor-anim--active .sensor-anim__mist {
  opacity: 0.55;
  animation: sensor-anim-mist 3.6s ease-in-out infinite;
}

.sensor-anim--active .sensor-anim__mist--b {
  animation-delay: 0.7s;
}

.sensor-anim--active .sensor-anim__mist--c {
  animation-delay: 1.4s;
}

@keyframes sensor-anim-mist {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-1.3px);
  }
}

/* Gas is the one cloud drawn in its own colour: yellow says "gas" before the
   detector does. */
.sensor-anim__mist--gas {
  fill: #facc15;
}

/* ── Fire: translucent ink, so the burn shows through itself. A clear room has
      no flame in it. ───────────────────────────────────────────────────────── */
.sensor-anim__flame {
  fill: color-mix(in srgb, currentColor 20%, transparent);
  stroke: color-mix(in srgb, currentColor 70%, transparent);
  stroke-width: 1.4;
  stroke-linejoin: round;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sensor-anim__flame-core {
  fill: color-mix(in srgb, currentColor 46%, transparent);
  stroke: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sensor-anim__flame-glow {
  fill: color-mix(in srgb, currentColor 12%, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sensor-anim--active .sensor-anim__flame,
.sensor-anim--active .sensor-anim__flame-core,
.sensor-anim--active .sensor-anim__flame-glow {
  opacity: 1;
}

.sensor-anim--active .sensor-anim__flame,
.sensor-anim--active .sensor-anim__flame-core {
  animation: sensor-anim-flicker 2.8s ease-in-out infinite;
}

@keyframes sensor-anim-flicker {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.72;
  }
}

/* ── Water: what the flood leaves, drawn the way the water valve draws it — a
      blue sheet across the floor with a lighter current running over it, and
      the ripples spreading out. ─────────────────────────────────────────────── */
.sensor-anim__flood {
  fill: color-mix(in srgb, #38bdf8 32%, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sensor-anim--active .sensor-anim__flood {
  opacity: 1;
}

.sensor-anim__flood-surface {
  fill: none;
  stroke: #38bdf8;
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-opacity: 0;
  transition: stroke-opacity 0.3s ease;
}

.sensor-anim--active .sensor-anim__flood-surface {
  stroke-opacity: 1;
}

.sensor-anim__flow {
  fill: none;
  stroke: #e0f2fe;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-dasharray: 3 7;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sensor-anim--active .sensor-anim__flow {
  opacity: 1;
  animation: sensor-anim-water 0.9s linear infinite;
}

@keyframes sensor-anim-water {
  to {
    stroke-dashoffset: -10;
  }
}

.sensor-anim__ripple {
  fill: none;
  stroke: #38bdf8;
  stroke-width: 1.2;
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
}

.sensor-anim--active .sensor-anim__ripple {
  animation: sensor-anim-ripple 2.6s ease-out infinite;
}

.sensor-anim--active .sensor-anim__ripple--b {
  animation-delay: 1.3s;
}

@keyframes sensor-anim-ripple {
  0% {
    transform: scale(0.35);
    opacity: 0.85;
  }
  70% {
    opacity: 0.3;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

/* Reduced motion: the state still shows, it just does not move or fade in. */
@media (prefers-reduced-motion: reduce) {
  .sensor-anim__flame,
  .sensor-anim__flame-core,
  .sensor-anim__flame-glow,
  .sensor-anim__flood,
  .sensor-anim__flood-surface,
  .sensor-anim__flow,
  .sensor-anim__haze,
  .sensor-anim__mist,
  .sensor-anim__person,
  .sensor-anim__trace {
    transition: none;
  }

  .sensor-anim--active .sensor-anim__flame,
  .sensor-anim--active .sensor-anim__flame-core,
  .sensor-anim--active .sensor-anim__flow,
  .sensor-anim--active .sensor-anim__mist,
  .sensor-anim--active .sensor-anim__ripple {
    animation: none;
  }
}
</style>
